import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, ExternalLink, PenLine, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeading, PrimaryBtn, OutlineBtn } from "./shared";
import { StarRow } from "./StarPicker";

const track = (event) => {
  try { if (typeof window !== "undefined" && typeof window.gtag === "function") window.gtag("event", event); } catch (e) {}
};

const ReviewCard = ({ r }) => {
  const [open, setOpen] = useState(false);
  const long = (r.text || "").length > 220;
  const shown = open || !long ? r.text : `${r.text.slice(0, 220)}…`;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 card-shadow flex flex-col" data-testid="review-card">
      <div className="flex items-center justify-between gap-2">
        <StarRow n={r.rating || 5} />
        {r.verified && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--action)] bg-[var(--action)]/10 px-2 py-0.5 rounded-full" data-testid="verified-badge">
            <ShieldCheck className="w-3 h-3" /> Verified Customer
          </span>
        )}
      </div>
      <p className="text-[var(--text-dark)] mt-3 leading-relaxed break-words">"{shown}"</p>
      {long && (
        <button onClick={() => setOpen(!open)} className="text-sm font-semibold text-[var(--action)] mt-2 self-start" data-testid="review-read-more">
          {open ? "Read Less" : "Read More"}
        </button>
      )}
      <div className="mt-4 pt-3 border-t border-gray-50">
        <p className="font-semibold text-[var(--navy)]">{r.name}</p>
        <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5 flex-wrap">
          {r.service && <span>{r.service}</span>}
          {r.source === "imported_google" && (r.source_label || "Originally posted on Google") && (
            <span>· {r.source_label || "Originally posted on Google"}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const RatingHeader = ({ average, count }) => {
  if (average == null || !count) return null;
  return (
    <div className="flex flex-col items-center gap-1 mb-10" data-testid="rating-header">
      <StarRow n={average} size="w-6 h-6" />
      <p className="text-2xl font-head font-extrabold text-[var(--navy)]">{Number(average).toFixed(1)}</p>
      <p className="text-sm text-gray-500">Based on {count} Customer Review{count === 1 ? "" : "s"}</p>
    </div>
  );
};

export default function CustomerReviews({ mode = "full" }) {
  const nav = useNavigate();
  const home = mode === "home";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ average: null, count: 0 });
  const [config, setConfig] = useState({});
  const [limit, setLimit] = useState(home ? 6 : 12);

  const loadList = (lim) => api.get(`/reviews?limit=${lim}`).then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); }).catch(() => {});
  useEffect(() => {
    loadList(limit);
    api.get("/reviews/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/reviews/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const loadMore = () => { const l = limit + 9; setLimit(l); loadList(l); };
  const goLeave = () => { track("review_form_opened"); nav("/reviews/leave"); };

  return (
    <section className="section-pad" data-testid="customer-reviews-section">
      <div className="max-w-7xl mx-auto px-4">
        {home && <SectionHeading overline="Customer Reviews" title="What Our Customers Say" center />}

        <RatingHeader average={summary.average} count={summary.count} />

        {items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((r) => <ReviewCard key={r.id} r={r} />)}
          </div>
        ) : (
          <p className="text-center text-[var(--text-muted)]" data-testid="reviews-empty">
            Be the first to share your experience with Veterans Power Wash.
          </p>
        )}

        {!home && total > items.length && (
          <div className="text-center mt-8">
            <OutlineBtn onClick={loadMore} data-testid="load-more-reviews">Load More Reviews</OutlineBtn>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3 mt-10">
          <PrimaryBtn onClick={goLeave} data-testid="leave-review-btn"><PenLine className="w-4 h-4" /> Leave a Review</PrimaryBtn>
          {home ? (
            <OutlineBtn onClick={() => { track("reviews_view_all_clicked"); nav("/reviews"); }} data-testid="read-all-reviews-btn">
              Read All Reviews
            </OutlineBtn>
          ) : (
            config.google_leave_url && (
              <OutlineBtn href={config.google_leave_url} target="_blank" rel="noopener noreferrer"
                onClick={() => track("google_review_clicked")} data-testid="review-on-google-btn">
                <ExternalLink className="w-4 h-4" /> Review Us on Google
              </OutlineBtn>
            )
          )}
        </div>
      </div>
    </section>
  );
}
